import { OAuthProvider } from '@seapoint/server/enums';
import { format } from 'date-fns';
import { ComponentProps, ReactNode } from 'react';
import logoGoogle from '../../../assets/images/google-logo.svg';
import logoXero from '../../../assets/images/logo-xero.svg';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { AllowedOAuthProvider } from '../types/types';

const GMAIL_URL = 'https://mail.google.com/';
const XERO_URL = 'https://go.xero.com/';

const DATE_FORMAT = 'dd MMM yyyy';

type ConnectedOAuthProviderCardProps = {
  provider: AllowedOAuthProvider;
  displayName?: string;
  connectionDate?: Date;
  onDisconnect: () => void;
};

export const ConnectedOAuthProviderCard = ({
  provider,
  displayName,
  connectionDate,
  onDisconnect,
}: ConnectedOAuthProviderCardProps) => {
  const { logo, name, title, displayUrl, destinationUrl } = getCardContent(provider);
  const formattedConnectionDate = connectionDate ? format(connectionDate, DATE_FORMAT) : undefined;

  return (
    <Stack space={4} className="rounded-[10px] bg-seapoint-gray-background-modal p-4">
      <Inline justify="between" className="w-full gap-y-3">
        <Inline space={20} className="gap-y-3">
          <Inline space={3}>
            <div className="h-8 w-8 rounded-full">
              <img src={logo} alt="" loading="eager" fetchPriority="high" className="h-full w-full object-cover" />
            </div>

            <h3 className="text-sm font-medium leading-[22px] tracking-tight text-black">{name}</h3>
          </Inline>

          <p className="text-xs font-normal leading-[22px] tracking-tight text-seapoint-gray-text">{title}</p>
        </Inline>

        <Button variant="destructive" onClick={onDisconnect} className="border-0">
          Disconnect
        </Button>
      </Inline>

      <div className="h-px w-full bg-seapoint-gray-background-hover"></div>

      <Inline space={12} className="gap-y-3">
        {displayName && <OauthConnectionInfoItems label="Organisation" value={displayName} />}

        {formattedConnectionDate && <OauthConnectionInfoItems label="Added on" value={formattedConnectionDate} />}

        <OauthConnectionInfoItems
          label="Website"
          value={
            <a
              href={destinationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-normal leading-[22px] tracking-tight text-seapoint-blue-link"
            >
              {displayUrl}
            </a>
          }
        />

        <OauthConnectionInfoItems
          label="Status"
          value={
            <Inline space={1.5}>
              <span
                data-testid="connection-status-icon"
                aria-hidden
                className="h-1.5 w-1.5 rounded-full bg-seapoint-green-badge"
              />
              <p className="text-xs font-medium leading-6 tracking-tight text-black">Connected</p>
            </Inline>
          }
        />
      </Inline>
    </Stack>
  );
};

type OauthConnectionInfoItemsProps = {
  label: string;
  value: string | ReactNode;
  className?: string;
};

const OauthConnectionInfoItems = ({ label, value, className }: OauthConnectionInfoItemsProps) => {
  return (
    <Stack space={1} className={className}>
      <p className="text-xs font-normal leading-[22px] tracking-tight text-seapoint-gray-text">{label}</p>

      {typeof value === 'string' ? (
        <p className="text-sm font-normal leading-[22px] tracking-tight text-black">{value}</p>
      ) : (
        value
      )}
    </Stack>
  );
};

const getCardContent = (provider: ComponentProps<typeof ConnectedOAuthProviderCard>['provider']) => {
  switch (provider) {
    case OAuthProvider.XERO:
      return {
        logo: logoXero,
        name: 'Xero',
        title: 'Sync Seapoint with Xero to keep your books clean and your team in control.',
        displayUrl: 'xero.com',
        destinationUrl: XERO_URL,
      };
    case OAuthProvider.GMAIL:
      return {
        logo: logoGoogle,
        name: 'Gmail',
        title: `We'll automatically fetch all invoices from your inbox and link them to the corresponding transactions`,
        displayUrl: 'gmail.com',
        destinationUrl: GMAIL_URL,
      };
    default:
      return exhaustiveCheck(provider);
  }
};
